import { useEffect, useRef, useState } from "react";
import { mergeAttributes } from "@tiptap/core";
import { Image } from "@tiptap/extension-image";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";

// Drag-corner resize handle + left/center/right alignment for images dropped into
// RichTextEditor. Width/align are stored as node attrs and rendered as an inline
// `style` on the <img> itself (not a wrapper) so the HTML still looks right once it's
// pulled out of Tiptap and reused verbatim in the fighter welcome email - sanitizeHtml's
// ALLOWED_ATTR already permits `style`, so nothing there needed to change.
function ResizableImageView({ node, updateAttributes, selected }) {
  const { src, alt, width, align } = node.attrs;
  const imgRef = useRef(null);
  const [widthDraft, setWidthDraft] = useState(String(width || ""));
  useEffect(() => {
    if (selected) setWidthDraft(String(width || imgRef.current?.offsetWidth || ""));
  }, [width, selected]);

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = imgRef.current.offsetWidth;
    const onMove = (ev) => {
      const next = Math.max(40, Math.round(startWidth + (ev.clientX - startX)));
      updateAttributes({ width: next });
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  const wrapperStyle = {
    position: "relative",
    display: align === "left" ? "inline-block" : "block",
    float: align === "left" ? "left" : "none",
    marginRight: align === "left" ? "1em" : undefined,
    marginLeft: align === "center" ? "auto" : undefined,
    width: width ? `${width}px` : "auto",
    maxWidth: "100%",
  };
  if (align === "center") {
    wrapperStyle.marginLeft = "auto";
    wrapperStyle.marginRight = "auto";
  } else if (align === "right") {
    wrapperStyle.marginLeft = "auto";
  }

  return (
    <NodeViewWrapper
      as="div"
      style={wrapperStyle}
      className={selected ? "ring-2 ring-green-action-text rounded" : ""}
    >
      <img ref={imgRef} src={src} alt={alt ?? ""} draggable={false} style={{ width: "100%", display: "block" }} />
      {selected && (
        <div
          contentEditable={false}
          draggable={false}
          className="flex flex-wrap gap-1 mt-1 items-center bg-dark-800 p-1 rounded-lg">
          <button type="button" onClick={() => updateAttributes({ width: Math.max(40, (width || imgRef.current.offsetWidth) - 40) })} aria-label="Decrease image width" className="min-w-6 min-h-6 px-1 border border-gray-300 rounded">−</button>
          <span className="text-xs text-white">{width || imgRef.current?.offsetWidth || 0}px wide</span>
          <label className="text-xs text-white">Width in pixels
            <input type="number" min="40" value={widthDraft} onChange={(e) => setWidthDraft(e.target.value)}
              onBlur={() => { const next = Number(widthDraft); if (next >= 40) updateAttributes({ width: Math.round(next) }); else setWidthDraft(String(width || imgRef.current?.offsetWidth || "")); }}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              className="w-20 ml-1 p-1 bg-dark-900 border border-gray-300 rounded text-white" />
          </label>
          <button type="button" onClick={() => updateAttributes({ width: (width || imgRef.current.offsetWidth) + 40 })} aria-label="Increase image width" className="min-w-6 min-h-6 px-1 border border-gray-300 rounded">+</button>
          <button type="button" onPointerDown={startResize} aria-label="Drag to resize image" className="min-w-6 min-h-6 px-1 border border-gray-300 rounded cursor-nwse-resize">↘</button>
        </div>
      )}
    </NodeViewWrapper>
  );
}

export const ResizableImage = Image.extend({
  draggable: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: null },
      align: { default: null },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const { width, align } = node.attrs;
    const styles = [];
    if (width) styles.push(`width:${width}px`, "height:auto");
    if (align === "center") styles.push("display:block", "margin-left:auto", "margin-right:auto");
    else if (align === "right") styles.push("display:block", "margin-left:auto", "margin-right:0");
    else if (align === "left") styles.push("float:left", "margin:0 1em 0.5em 0");
    return [
      "img",
      mergeAttributes(HTMLAttributes, styles.length ? { style: styles.join(";") } : {}),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});
